import { Grid, Typography } from '@mui/material';

import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import ButtonComp from 'common/components/Button/ButtonComp';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { hasEmptyValues, toSentenceCase } from 'common/utils/utilFunctions';

import { fetchTestResult } from '../../api';
import { IPromptProps } from '../../api/type-def';

import { TYPE_DISPLAY_MAPPING, TYPE_OPTIONS } from '../PromptBuilderConstants';

import {
	findVariablesInPrompt,
	replaceVariablesInPrompt,
} from './TestPaneUtils';

import styles from './TestPane.module.scss';

interface ITestPane {
	prompt: IPromptProps;
}

const TestPane = (props: ITestPane) => {
	const { prompt } = props;
	const [inputValue, setInputValue] = useState<string>('');
	const [result, setResult] = useState<string | undefined | null>('');
	const [testLoading, setTestLoading] = useState<boolean>(false);

	const { CHAT_COMPLETION } = TYPE_OPTIONS;
	const { CHAT_COMPLETION_DISPLAY, COMPLETION_DISPLAY } = TYPE_DISPLAY_MAPPING;
	const dispatch = useDispatch();

	useEffect(() => {
		const inputStr = JSON.stringify(findVariablesInPrompt(prompt?.prompt));
		if (!inputValue) {
			setInputValue(inputStr);
		}
	}, [prompt]);

	const handleInputChange = (event: any) => {
		setInputValue(event.target.value);
		if (!event.target.value) {
			setResult('');
		}
	};

	const { OK } = HTTP_STATUS_CODES;

	const handleButtonClick = async () => {
		setTestLoading(true);
		setResult('');
		let formattedPrompt;
		try {
			formattedPrompt = replaceVariablesInPrompt(
				prompt?.prompt,
				JSON.parse(inputValue),
			);
		} catch (error) {
			dispatch(
				setToastMessage({
					message:
						'Invalid JSON format in input. Please correct it before testing.',
					severity: 'error',
					isToastOpen: true,
				}),
			);
			setTestLoading(false);
			return; // Exit function if JSON is invalid
		}
		const payload = {
			messages: prompt.messages,
			parameters: prompt.parameters,
			prompt: formattedPrompt,
			type:
				prompt.type === CHAT_COMPLETION
					? CHAT_COMPLETION_DISPLAY
					: COMPLETION_DISPLAY,
		};
		const res = await fetchTestResult(payload);
		if (res.statusCode === OK) {
			setResult(res?.responseBody?.answer);
			setTestLoading(false);
		} else if (res?.errorBody || res?.error) {
			const { errorBody } = res;
			const responseError: any = res.error;
			const errorMessage =
				(errorBody?.error as string) || (responseError?.message as string);
			dispatch(
				setToastMessage({
					message: errorMessage,
					severity: 'error',
					isToastOpen: true,
				}),
			);
			setTestLoading(false);
		}
	};

	return (
		<>
			<Grid container spacing={2}>
				<Grid item xs={6}>
					<Typography className={styles['sample-header']}>
						Sample Input
					</Typography>
					<TextFieldWithLabel
						placeholder="Enter sample input"
						inputProps={{
							'data-testid': 'sample-input',
						}}
						error={false}
						required
						value={inputValue}
						onChange={handleInputChange}
						parentClass={styles['word-txt-position']}
						helperText=""
						showLabel={false}
						fullWidth
						multiLine
						className={styles['sample-inp-out']}
					/>
				</Grid>
				<Grid item xs={6}>
					<Typography className={styles['sample-header']}>
						Sample Result
					</Typography>
					<TextFieldWithLabel
						placeholder="No results"
						inputProps={{
							'data-testid': 'sample-result',
						}}
						error={false}
						required
						value={result}
						onChange={handleInputChange}
						parentClass={styles['word-txt-position']}
						helperText=""
						showLabel={false}
						fullWidth
						multiLine
						className={styles['sample-inp-out']}
						readOnly
					/>
				</Grid>
			</Grid>
			<ButtonComp
				btnType="negative"
				clickHandler={handleButtonClick}
				fullWidth={false}
				disabled={
					!inputValue || hasEmptyValues(JSON.parse(JSON.stringify(inputValue)))
				}
				data-testid="test-prompt-btn"
				loading={testLoading}
			>
				{toSentenceCase('Test prompt')}
			</ButtonComp>{' '}
		</>
	);
};

export default TestPane;
